import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import AppDialog from "@/components/common/AppDialog";
import { PATHS } from "@/lib/router";
import { ROLES } from "@/lib/constants/options";
import { useAuthStore } from "@/features/auth";
import { showToast } from "@/lib/utils/toast";
import SurveyList from "./SurveyList";
import SurveyShareDialog from "./SurveyShareDialog";
import {
  useSurveyShareStore,
  useSurveyTakeStore,
  useSurveysStore,
  useSurveysUiStore,
} from "../hooks";
import { surveysService } from "../services";

const StandardSurveysTab = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { openTake } = useSurveyTakeStore();
  const shareStore = useSurveyShareStore();
  const surveysUiStore = useSurveysUiStore();
  const {
    standardSurveys,
    standardLoading,
    standardError,
    loadStandardSurveys,
  } = useSurveysStore();
  const {
    standardDeleteTarget,
    standardDeleteLoading,
    openStandardDelete,
    closeStandardDelete,
    setStandardDeleteLoading,
  } = surveysUiStore;

  const isAdminRole = [ROLES.SYSTEM_ADMIN, ROLES.BUSINESS_UNIT].includes(
    user?.role,
  );

  useEffect(() => {
    loadStandardSurveys();
  }, [loadStandardSurveys]);

  const handlePublish = async (survey) => {
    if (!survey?._id) return;
    const publishPromise = surveysService.publishStandardSurvey(survey._id);
    showToast.promise(publishPromise, {
      loading: t("Publishing survey..."),
      success: t("Survey published."),
      error: t("Unable to publish survey."),
    });
    await publishPromise;
    loadStandardSurveys();
  };

  const handleArchive = async (survey) => {
    if (!survey?._id) return;
    const archivePromise = surveysService.archiveStandardSurvey(survey._id);
    showToast.promise(archivePromise, {
      loading: t("Archiving survey..."),
      success: t("Survey archived."),
      error: t("Unable to archive survey."),
    });
    await archivePromise;
    loadStandardSurveys();
  };

  const handleDelete = async () => {
    if (!standardDeleteTarget?._id) return;
    setStandardDeleteLoading(true);
    try {
      const deletePromise = surveysService.deleteStandardSurvey(
        standardDeleteTarget._id,
      );

      showToast.promise(deletePromise, {
        loading: t("Deleting survey..."),
        success: t("Survey deleted."),
        error: t("Unable to delete survey."),
      });
      await deletePromise;
      closeStandardDelete();
      loadStandardSurveys();
    } finally {
      setStandardDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {isAdminRole ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-radius border border-border bg-card/60 p-4 shadow-sm">
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {t("Standard Surveys")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t(
                "Review and manage standard survey templates used across organizations.",
              )}
            </p>
          </div>
          <Button
            type="button"
            onClick={() => navigate(PATHS.STANDARD_SURVEY_NEW)}
          >
            {t("Create Standard Survey")}
          </Button>
        </div>
      ) : null}

      {standardError ? (
        <p className="text-sm text-destructive">{standardError}</p>
      ) : null}

      {standardLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-32 w-full" />
          ))}
        </div>
      ) : (
        <SurveyList
          surveys={standardSurveys}
          actionLabel={t("Start Survey")}
          secondaryActionLabel={t("View Details")}
          onSelect={(survey) => openTake(survey)}
          onSecondaryAction={(survey) =>
            navigate(
              PATHS.STANDARD_SURVEY_EDIT.replace(
                ":surveyId",
                survey?._id || "",
              ),
            )
          }
          showPrimaryAction={!isAdminRole}
          showShareButton={!isAdminRole}
          onShare={(survey) => shareStore.openDialog(survey)}
          showQuestionCountBubble={false}
          showCategoryBadge={false}
          showResponseCount={false}
          canEditStandard={isAdminRole}
          onEditStandard={(survey) =>
            navigate(
              PATHS.STANDARD_SURVEY_EDIT.replace(
                ":surveyId",
                survey?._id || "",
              ),
            )
          }
          canPublishStandard={isAdminRole}
          onPublishStandard={handlePublish}
          onArchiveStandard={handleArchive}
          canDeleteStandard={isAdminRole}
          onDeleteStandard={(survey) => openStandardDelete(survey)}
        />
      )}

      {standardDeleteTarget ? (
        <AppDialog
          open
          onOpenChange={(open) => {
            if (!open) closeStandardDelete();
          }}
          title={t("Delete survey")}
          description={t("This will permanently delete the survey.")}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeStandardDelete}>
                {t("Cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={standardDeleteLoading}
              >
                {standardDeleteLoading ? t("Deleting...") : t("Delete")}
              </Button>
            </div>
          }
        />
      ) : null}

      <SurveyShareDialog />
    </div>
  );
});

export default StandardSurveysTab;
